import React from 'react';
import CodeInput from 'react-verification-code-input';
import { ButtonV2 } from '@bbkAdminComponents/buttons';
import { Label } from '@bbkAdminComponents/inputs/label/label';
import { PasswordInput } from '@bbkAdminComponents/inputs/password/password-input';
import { LogInError } from '@bbkAdminComponents/log-in-flow/error';
import { LogInPaths } from '@bbkAdminComponents/log-in-flow/log-in-paths';
import type * as LogInTypes from '@bbkAdminComponents/log-in-flow/log-in-types';
import { LogInSuccess } from '@bbkAdminComponents/log-in-flow/success';
import { LogInTrouble } from '@bbkAdminComponents/log-in-flow/trouble';
import { TippyTooltip } from '@bbkAdminComponents/tooltips/tippy-tooltip';
import { Icons } from '@bbkAdminUtils/icons';

type ResetTypes = {
  error?: LogInTypes.Error;
  success?: LogInTypes.Success;
  formValues: Partial<Omit<LogInTypes.FormValues, 'setPassword'>> &
    Pick<LogInTypes.FormValues, 'setPassword'>;
  updatePassword: LogInTypes.UpdatePassword;
  sending: LogInTypes.Sending;
  disabled: LogInTypes.Disabled;
};

export const ResetPassword: React.FC<ResetTypes> = ({
  error,
  success,
  formValues,
  updatePassword,
  sending,
  disabled,
}) => {
  const { password, setPassword, setPasswordVerifyCode, passwordVerifyCode } =
    formValues;
  return (
    <div>
      <form target="#" onSubmit={updatePassword}>
        <div className="content">
          <div className="tw-mb-4 tw-font-semibold">Almost there...</div>
          {success && <LogInSuccess success={success} />}
          {error && <LogInError error={error} />}
          <div className="verify">
            <label>
              We have attempted to send a 6-digit verification code to you.
              Please paste or type it below.
            </label>
          </div>
          <div className="tw-inline-flex">
            <CodeInput
              type="number"
              fields={6}
              autoFocus={true}
              onComplete={setPasswordVerifyCode}
              className={`[&_input::selection]:tw-bg-[#f3e7f9] [&_input:focus]:tw-border-[#a45fe1] [&_input:focus]:tw-bg-[#f5f0f8] [&_input:focus]:tw-caret-[#9014fe] [&_input:last-child]:tw-border-r-2 [&_input:last-child]:tw-border-r-[#9014fe] [&_input]:tw-border-2 [&_input]:tw-border-r-0 [&_input]:tw-border-solid [&_input]:tw-border-[#9014fe] [&_input]:tw-bg-[#f3e7f9] [&_input]:tw-text-center [&_input]:tw-font-[inherit] [&_input]:tw-text-2xl`}
            />
          </div>
          <div className="verify">
            Didn't get a code?
            <TippyTooltip
              content={
                <p className="tw-text-sm">
                  Please ensure you are entering your email address exactly how
                  you did when signing up. Please check case and other special
                  characters.
                </p>
              }
            >
              <span className="tw-ml-1">{Icons.questionMark}</span>
            </TippyTooltip>
          </div>

          <div className="verify form-container">
            {passwordVerifyCode && (
              <div className="helpContainer">
                <Label
                  id="password"
                  helpText="12 character minimum"
                  className="tw-text-sm tw-font-semibold"
                >
                  New password
                </Label>
                <PasswordInput
                  name="password"
                  autoComplete="on"
                  placeholder="Password"
                  required
                  pattern=".{12,}"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={sending}
                  autoFocus={true}
                />
              </div>
            )}
          </div>
          {!disabled && (
            <ButtonV2
              primary
              className="tw-w-full"
              type="submit"
              disabled={sending}
            >
              Reset password
            </ButtonV2>
          )}
        </div>
      </form>
      <LogInTrouble link={{ path: LogInPaths.signIn, copy: 'Sign in' }} />
    </div>
  );
};
